import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Building2, Plus, MapPin, DollarSign, Percent, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { getProperties, deleteProperty, formatCurrency, formatPercent } from "@/lib/api";

const PropertyCard = ({ property, onDelete }) => {
  const equity = property.current_value - property.loan_amount;
  const lvr = property.current_value > 0 
    ? (property.loan_amount / property.current_value) * 100 
    : 0;
  const rentalYield = property.current_value > 0 
    ? ((property.rental_income_weekly * 52) / property.current_value) * 100 
    : 0;

  return (
    <Card className="overflow-hidden card-hover group" data-testid={`property-card-${property.id}`}>
      <div className="h-40 bg-gradient-to-br from-slate-100 to-slate-200 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <Building2 className="w-16 h-16 text-slate-300" />
        </div>
        <div className="absolute top-3 right-3">
          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-slate-700 capitalize">
            {property.property_type}
          </span>
        </div>
      </div>
      <CardContent className="p-5">
        <div className="space-y-4">
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 line-clamp-1">
              {property.address}
            </h3>
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
              <MapPin className="w-3.5 h-3.5" />
              {property.suburb}, {property.state} {property.postcode}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Value</p>
              <p className="font-semibold text-slate-900">{formatCurrency(property.current_value)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Equity</p>
              <p className="font-semibold text-emerald-600">{formatCurrency(equity)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">LVR</p>
              <p className="font-semibold text-slate-900">{formatPercent(lvr)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Yield</p>
              <p className="font-semibold text-slate-900">{formatPercent(rentalYield)}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <Link to={`/properties/${property.id}`} className="flex-1">
              <Button 
                variant="outline" 
                className="w-full rounded-full"
                data-testid={`view-property-${property.id}`}
              >
                View Details
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="text-slate-400 hover:text-red-500 hover:bg-red-50"
              onClick={(e) => {
                e.preventDefault();
                onDelete(property);
              }}
              data-testid={`delete-property-${property.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchProperties = async () => {
    try {
      const response = await getProperties();
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Failed to load properties");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      await deleteProperty(deleteTarget.id);
      setProperties(properties.filter(p => p.id !== deleteTarget.id));
      toast.success("Property deleted successfully");
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error("Failed to delete property");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
            Properties
          </h2>
          <p className="text-slate-500 mt-1">
            Manage your property portfolio
          </p>
        </div>
        <Link to="/properties/new">
          <Button 
            className="bg-[#547d91] hover:bg-[#456a7a] text-white rounded-full px-6"
            data-testid="add-new-property-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        </Link>
      </div>

      {/* Properties Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="h-40" />
              <CardContent className="p-5 space-y-4">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : properties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property) => (
            <PropertyCard 
              key={property.id} 
              property={property} 
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      ) : (
        <Card className="p-12">
          <div className="text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">
              No properties yet
            </h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Start tracking your property investments by adding your first property to the portfolio
            </p>
            <Link to="/properties/new">
              <Button 
                className="bg-[#547d91] hover:bg-[#456a7a] text-white rounded-full px-8"
                data-testid="add-first-property-empty"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Property
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.address}"? This action cannot be undone and will also remove all associated expenses.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
